import { ArrowRight } from 'lucide-react';
import type { FC, ReactNode } from 'react';

interface FlowStatProps {
  inLabel: string;
  inValue: ReactNode;
  outLabel: string;
  outValue: ReactNode;
  /** Beside the output: a delta such as `-87%`, or a phrase. */
  note?: ReactNode;
  /** Share of the input that survives, 0–1. Draws the bar under the numbers. */
  share?: number | null;
}

/** Before → after for one step: `ROWS IN 48.2M → ROWS OUT 6.1M -87%`. */
export const FlowStat: FC<FlowStatProps> = ({
  inLabel,
  inValue,
  outLabel,
  outValue,
  note,
  share
}) => (
  <div className="rounded-[12px] border border-border bg-surface-hover px-4 py-3.5">
    <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-4">
      <div>
        <p className="text-eyebrow-sm text-muted-foreground uppercase">{inLabel}</p>
        <p className="text-title-sm text-foreground tabular-nums">{inValue}</p>
      </div>
      <ArrowRight aria-hidden="true" className="mb-1.5 size-4 text-muted-foreground" />
      <div>
        <p className="text-eyebrow-sm text-muted-foreground uppercase">{outLabel}</p>
        <p className="flex items-baseline gap-2 text-title-sm text-foreground tabular-nums">
          {outValue}
          {note ? (
            <span className="text-caption font-normal text-[color:var(--z-text-secondary)]">
              {note}
            </span>
          ) : null}
        </p>
      </div>
    </div>
    {share != null ? (
      <div aria-hidden="true" className="mt-3 h-1 overflow-hidden rounded-pill bg-border">
        <div
          className="h-full rounded-pill bg-brand"
          style={{ width: `${Math.max(Math.min(share, 1), 0.01) * 100}%` }}
        />
      </div>
    ) : null}
  </div>
);
